import { endOfDay, formatISO, startOfYear } from "date-fns";
import type {
    DateOnlyRange,
    DatePickerPreset,
} from "@design-system/DatePicker/DatePicker.types";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import { SchedulesReportDateBounds } from "@common/schemas/domains/accounting/schedules-report.schema";
import { parseDateOnly } from "./schedulesReportDates";

export const THROUGH_END_OF_SCHEDULES_PRESET_ID = "through_end_of_schedules";

export const SCHEDULES_REPORT_DATE_PRESET_IDS = [
    "this_year_to_date",
    "this_year",
    "last_year",
    "last_12_months",
    "next_12_months",
    "through_end_of_next_year",
    THROUGH_END_OF_SCHEDULES_PRESET_ID,
] as const;

export type SchedulesReportDatePresetId =
    (typeof SCHEDULES_REPORT_DATE_PRESET_IDS)[number];

export const DEFAULT_SCHEDULES_REPORT_DATE_PRESET: SchedulesReportDatePresetId =
    "this_year_to_date";

export const SCHEDULES_REPORT_DATE_PRESET_LABELS: Record<
    SchedulesReportDatePresetId,
    string
> = {
    this_year_to_date: "This year to date",
    this_year: "This year",
    last_year: "Last year",
    last_12_months: "Last 12 months",
    next_12_months: "Next 12 months",
    through_end_of_next_year: "Through end of next year",
    [THROUGH_END_OF_SCHEDULES_PRESET_ID]: "Through end of schedules",
};

export function isSchedulesReportDatePresetId(
    value: string | null | undefined,
): value is SchedulesReportDatePresetId {
    return SCHEDULES_REPORT_DATE_PRESET_IDS.some((id) => id === value);
}

interface ResolvePresetRangeParams {
    presetId: SchedulesReportDatePresetId;
    today: Date;
    dateBounds?: SchedulesReportDateBounds;
}

/** Returns null while "Through end of schedules" is waiting for the bounds or has none to use. */
export function resolveSchedulesReportPresetRange({
    presetId,
    today,
    dateBounds,
}: ResolvePresetRangeParams): { start: Date; end: Date } | null {
    if (presetId === THROUGH_END_OF_SCHEDULES_PRESET_ID) {
        if (!dateBounds?.maxTermEnd) {
            return null;
        }
        return {
            start: startOfYear(today),
            end: endOfDay(parseDateOnly(dateBounds.maxTermEnd)),
        };
    }
    const preset = DATE_RANGE_PRESETS_BY_ID[presetId];
    return { start: preset.getStart(today), end: preset.getEnd(today) };
}

const EXTRA_DATE_FILTER_PRESET_IDS: SchedulesReportDatePresetId[] = [
    "next_12_months",
    "through_end_of_next_year",
    THROUGH_END_OF_SCHEDULES_PRESET_ID,
];

function toDateOnlyString(date: Date | undefined): string | undefined {
    return date ? formatISO(date, { representation: "date" }) : undefined;
}

interface PresetContext {
    today: Date;
    dateBounds?: SchedulesReportDateBounds;
}

/** Presets the shared date filter does not offer on its own. */
export function buildSchedulesReportExtraPresets({
    today,
    dateBounds,
}: PresetContext): Array<DatePickerPreset<DateOnlyRange>> {
    return EXTRA_DATE_FILTER_PRESET_IDS.flatMap((presetId) => {
        const range = resolveSchedulesReportPresetRange({
            presetId,
            today,
            dateBounds,
        });
        if (!range) {
            return [];
        }
        return [
            {
                id: presetId,
                label: SCHEDULES_REPORT_DATE_PRESET_LABELS[presetId],
                value: {
                    from: toDateOnlyString(range.start) ?? "",
                    to: toDateOnlyString(range.end) ?? "",
                },
            },
        ];
    });
}

interface MatchPresetParams extends PresetContext {
    start: Date | undefined;
    end: Date | undefined;
}

/** The preset whose range is exactly the given calendar days, if any. */
export function findMatchingSchedulesReportPreset({
    start,
    end,
    today,
    dateBounds,
}: MatchPresetParams): SchedulesReportDatePresetId | null {
    if (!start || !end) {
        return null;
    }
    const startDay = toDateOnlyString(start);
    const endDay = toDateOnlyString(end);
    return (
        SCHEDULES_REPORT_DATE_PRESET_IDS.find((presetId) => {
            const range = resolveSchedulesReportPresetRange({
                presetId,
                today,
                dateBounds,
            });
            return (
                !!range &&
                toDateOnlyString(range.start) === startDay &&
                toDateOnlyString(range.end) === endDay
            );
        }) ?? null
    );
}
